import React, { useState, useMemo } from 'react';
import { View, Text, TouchableOpacity, Linking, StyleSheet, Platform } from 'react-native';
import FontFamily from '../../../../constants/fonts';
import { useTheme } from '../../../../theme/useTheme';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../../store';
import {
  HpEvent,
  createHomepageUpcomingEventFromRedux,
  updateHomepageUpcomingEventFromRedux,
  deleteHomepageUpcomingEvent,
} from '../../../../store/slices/adminSlice';
import LHDataTable from '../../../../components/LHDataTable';
import { AdminColors } from '../../constants/adminColors';
import { useToast } from '../../../../components/LhToastContainer';
import AdminTag from '../../components/AdminTag';
import AdminButton from '../../components/AdminButton';
import AdminModal from '../../components/AdminModal';
import { AdminFormField, AdminTextInput } from '../../components/AdminFormField';
import LhDatePicker from '../../../../components/LhDatePicker';
import RowActions from './RowActions';
import DeleteConfirmModal from './DeleteConfirmModal';
import EmptyState from './EmptyState';
import { styles } from './styles';
import { formatDate, computeEventStatus } from './helpers';
import { formatUserName } from '../../../../utils/emailTouserName';
import { formatDate as formatDateTime } from '../../../../utils/dateFormatter';

const TIMEZONE_OPTIONS = ['EST', 'CST' , 'PST',];

const parseLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// ─── Inline dropdown: HTML <select> on web, custom picker on native ────────────
interface SimpleSelectProps {
  value: string;
  options: string[];
  onChange: (val: string) => void;
  width?: number;
}
const SimpleSelect: React.FC<SimpleSelectProps> = ({ value, options, onChange, width }) => {
  const [open, setOpen] = useState(false);
  const { colors, resolvedTheme } = useTheme();
  const isSelectDark = resolvedTheme === 'dark';
  const isWebDark = resolvedTheme === 'dark' && Platform.OS === 'web';

  if (Platform.OS === 'web') {
    return (
      <View style={[ssStyles.webWrap, width ? { width } : {}]}>
        {/* @ts-ignore – 'select' is valid on web */}
        <select
          value={value}
          onChange={(e: any) => onChange(e.target.value)}
          style={{
            height: 36,
            border: `1px solid ${isWebDark ? colors.input.border : isSelectDark ? '#444444' : AdminColors.borderDefault}`,
            borderRadius: 5,
            fontSize: 13,
            color: isWebDark ? colors.text.primary : isSelectDark ? '#FFFFFF' : AdminColors.textPrimary,
            backgroundColor: isWebDark ? colors.input.background : isSelectDark ? '#1a1a1a' : AdminColors.bgSurface,
            paddingLeft: 10,
            paddingRight: 28,
            fontFamily: FontFamily.regular,
            appearance: 'none',
            width: '100%',
            cursor: 'pointer',
            outline: 'none',
            colorScheme: isWebDark ? 'dark' : 'light',
          } as any}
        >
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <Text style={[[ssStyles.webChevron, isWebDark && { color: colors.text.secondary } as any], isSelectDark && { color: '#FFFFFF' }]}>{'\u2304'}</Text>
      </View>
    );
  }

  return (
    <View style={[ssStyles.nativeWrap, width ? { width } : {}]}>
      <TouchableOpacity
        style={[
          ssStyles.nativeBtn,
          isSelectDark && { backgroundColor: colors.input.background, borderColor: colors.input.border },
        ]}
        onPress={() => setOpen(p => !p)}
        activeOpacity={0.7}
      >
        <Text style={[ssStyles.nativeBtnLabel, isSelectDark && { color: colors.text.primary }]}>{value}</Text>
        <Text style={[ssStyles.nativeChevron, isSelectDark && { color: colors.text.disabled }]}>{open ? '▲' : '▼'}</Text>
      </TouchableOpacity>
      {open && (
        <View style={[ssStyles.nativeDropdown, isSelectDark && { backgroundColor: colors.background.elevated, borderColor: colors.border.default }]}>
          {options.map(o => (
            <TouchableOpacity
              key={o}
              style={[
                ssStyles.nativeOption,
                o === value && ssStyles.nativeOptionActive,
                isSelectDark && o === value && { backgroundColor: colors.background.surface },
              ]}
              onPress={() => { onChange(o); setOpen(false); }}
              activeOpacity={0.7}
            >
              <Text style={[
                ssStyles.nativeOptionText,
                o === value && ssStyles.nativeOptionTextActive,
                isSelectDark && { color: o === value ? colors.brand.primary : colors.text.primary },
              ]}>{o}</Text>
            </TouchableOpacity>
          ))}
        </View>
      )}
    </View>
  );
};

export interface EventsTabProps {
  showAdd: boolean;
  onCloseAdd: () => void;
}

const EventsTab: React.FC<EventsTabProps> = ({ showAdd, onCloseAdd }) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === 'dark' && Platform.OS !== 'web';
  const isAnyDark = resolvedTheme === 'dark';
  const datePickerColorScheme = resolvedTheme === 'dark' ? 'dark' : 'light';

  const dispatch = useDispatch<AppDispatch>();
  const toast = useToast();
  const events = useSelector((state: RootState) => state.configuration.homepageConfig.events);
  const [editItem, setEditItem] = React.useState<HpEvent | null>(null);
  const [eventName, setEventName] = useState('');
  const [eventDate, setEventDate] = useState<Date | null>(null);
  const [eventHHMM, setEventHHMM] = useState('');
  const [eventAmPm, setEventAmPm] = useState<'AM' | 'PM'>('AM');
  const [eventTimezone, setEventTimezone] = useState('CST');
  const [eventUrl, setEventUrl] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<HpEvent | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // ── helpers ────────────────────────────────────────────────────────────────
  const isValidHHMM = (t: string) => /^(0?[1-9]|1[0-2]):[0-5][0-9]$/.test(t.trim());
  const isValidUrl  = (u: string) => /^https?:\/\/.+\.[a-zA-Z]{2,}/.test(u.trim());

  const STATUS_ORDER: Record<string, number> = { Active: 0, Scheduled: 1, Expired: 2 };
  const sortedEvents = useMemo(() => [...events].sort((a, b) => {
    const statusDiff = (STATUS_ORDER[a.status] ?? 3) - (STATUS_ORDER[b.status] ?? 3);
    if (statusDiff !== 0) return statusDiff;
    const dateDiff = (a.date ?? '').localeCompare(b.date ?? '');
    if (dateDiff !== 0) return dateDiff;
    return (a.time ?? '').localeCompare(b.time ?? '');
  }), [events]);

  React.useEffect(() => {
    if (editItem) {
      setEventName(editItem.name);
      setEventDate(editItem.date ? parseLocalDate(editItem.date) : null);
      const parts = (editItem.time ?? '').split(' ');
      setEventHHMM(parts[0] ?? '');
      setEventAmPm((parts[1] as 'AM' | 'PM') || 'AM');
      setEventTimezone(editItem.timezone || 'CST');
      setEventUrl(editItem.url);
    } else if (!showAdd) {
      setEventName('');
      setEventDate(null);
      setEventHHMM('');
      setEventAmPm('AM');
      setEventTimezone('CST');
      setEventUrl('');
    }
  }, [editItem, showAdd]);

  const isDirty = editItem
    ? (
      eventName !== editItem.name ||
      formatDate(eventDate) !== editItem.date ||
      (eventHHMM + ' ' + eventAmPm) !== editItem.time ||
      eventTimezone !== (editItem.timezone || 'CST') ||
      eventUrl !== editItem.url
    )
    : (!!eventName || !!eventDate || !!eventHHMM || !!eventUrl);

  const doClose = () => {
    onCloseAdd();
    setEditItem(null);
    setEventName('');
    setEventDate(null);
    setEventHHMM('');
    setEventAmPm('AM');
    setEventTimezone('CST');
    setEventUrl('');
    setErrors({});
    setShowUnsavedWarning(false);
  };

  const handleClose = () => {
    if (isDirty) { setShowUnsavedWarning(true); return; }
    doClose();
  };
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const target = deleteTarget;
    try {
      setIsDeleting(true);
      await dispatch(deleteHomepageUpcomingEvent({ id: target.id })).unwrap();
      toast.showToast({ header: 'Success', subtext: 'Event deleted successfully.', variant: 'success' });
      setDeleteTarget(null);
    } catch (error: any) {
      toast.showToast({
        header: 'Error',
        subtext: error || 'Failed to delete event. Please try again.',
        variant: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };
  const handleSave = async () => {
    const errs: Record<string, string> = {};
    if (!eventName.trim()) errs.name = 'Event Name is required.';
    if (!eventDate) errs.date = 'Event Date is required.';
    if (!eventHHMM.trim()) {
      errs.time = 'Event Time is required.';
    } else if (!isValidHHMM(eventHHMM)) {
      errs.time = 'Please enter a valid time (HH:MM, e.g. 10:30).';
    } else if (eventDate) {
      const [hh, mm] = eventHHMM.split(':').map(Number);
      let hours24 = hh;
      if (eventAmPm === 'PM' && hh !== 12) hours24 = hh + 12;
      if (eventAmPm === 'AM' && hh === 12) hours24 = 0;
      // Convert the entered time (in the selected timezone) to a UTC timestamp for comparison.
      // Fixed offsets: EST = -5, CST = -6, PST = -8
      const TZ_OFFSETS: Record<string, number> = { EST: -5, CST: -6, PST: -8 };
      const tzOffsetHours = TZ_OFFSETS[eventTimezone] ?? -6;
      const eventUTCMs = Date.UTC(
        eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate(),
        hours24, mm, 0, 0
      ) - tzOffsetHours * 60 * 60 * 1000;
      if (eventUTCMs <= Date.now()) errs.time = 'Start Time Must be After Current Time.';
    }
    if (!eventUrl.trim()) errs.url = 'Event URL is required.';
    else if (!isValidUrl(eventUrl)) errs.url = 'Please enter a valid URL (e.g., https://example.com).';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});

    const date = formatDate(eventDate);
    const status = computeEventStatus(date);
    const time = eventHHMM + ' ' + eventAmPm;
    const today = new Date().toISOString().split('T')[0];
    const currentUser = 'Admin';
    setIsSaving(true);
    if (editItem) {
      const resultAction = await dispatch(updateHomepageUpcomingEventFromRedux({
        id: editItem.id,
        name: eventName,
        date,
        time,
        timezone: eventTimezone,
        url: eventUrl,
        locType: 'WH',
      }));

      if (updateHomepageUpcomingEventFromRedux.fulfilled.match(resultAction)) {
        toast.showToast({ header: 'Success', subtext: 'Event updated successfully.', variant: 'success' });
        doClose();
      } else {
        toast.showToast({
          header: 'Error',
          subtext: (resultAction.payload as string) || 'Failed to update event.',
          variant: 'error',
        });
      }
    } else {
      const resultAction = await dispatch(createHomepageUpcomingEventFromRedux({
        name: eventName,
        date,
        time,
        timezone: eventTimezone,
        url: eventUrl,
        locType: 'WH',
      }));

      if (createHomepageUpcomingEventFromRedux.fulfilled.match(resultAction)) {
        toast.showToast({ header: 'Success', subtext: 'Event created successfully.', variant: 'success' });
        doClose();
      } else {
        toast.showToast({
          header: 'Error',
          subtext: (resultAction.payload as string) || 'Failed to create event.',
          variant: 'error',
        });
      }
    }
    setIsSaving(false);
  };
  const modalVisible = showAdd || editItem !== null;

  const eventColumns = [
    {
      columnKey: 'name',
      header: 'Event Name',
      flexGrow: 2.6,
      renderCell: (item: HpEvent) => (
        <Text style={{ color: isDark ? colors.text.primary : '#3A3A3A', fontSize: 14 }} numberOfLines={2} ellipsizeMode="tail">{item.name}</Text>
      ),
    },
    { columnKey: 'date', header: 'Date', flexGrow: 2.6 },
    {
      columnKey: 'time',
      header: 'Time',
      flexGrow: 1.2,
      renderCell: (item: HpEvent) => (
        <Text style={{ fontSize: 12, color: isDark ? colors.text.primary : AdminColors.textPrimary }}>
          {item.time}{item.timezone ? ' ' + item.timezone : ''}
        </Text>
      ),
    },
    {
      columnKey: 'url',
      header: 'URL',
      width: 120,
      renderCell: (item: HpEvent) => (
        <TouchableOpacity onPress={() => Linking.openURL(item.url)} activeOpacity={0.7}>
          <Text style={{ color: isDark ? colors.brand.primary : AdminColors.blue700, fontSize: 12 }}>↗ Open</Text>
        </TouchableOpacity>
      ),
    },
    {
      columnKey: 'status',
      header: 'Status',
      flexGrow: 0.8,
      renderCell: (item: HpEvent) => (
        <AdminTag
          label={item.status}
          variant={item.status === 'Active' ? 'green' : item.status === 'Scheduled' ? 'blue' : 'orange'}
          small
        />
      ),
    },
    {
      columnKey: 'createdBy',
      header: 'Created By',
      flexGrow: 0.9,
      renderCell: (item: HpEvent) => (
        <Text style={{ fontSize: 12, color: isDark ? colors.text.secondary : AdminColors.textPrimary }}>
          {item.createdBy ? formatUserName(item.createdBy) : '-'}
        </Text>
      ),
    },
    {
      columnKey: 'createdDate',
      header: 'Created',
      flexGrow: 2.2,
      renderCell: (item: HpEvent) => (
        <Text style={{ fontSize: 12, color: isDark ? colors.text.secondary : AdminColors.textPrimary }}>
          {item.createdDate ? formatDateTime(item.createdDate) : '-'}
        </Text>
      ),
    },
    {
      columnKey: 'modifiedBy',
      header: 'Modified By',
      flexGrow: 0.9,
      renderCell: (item: HpEvent) => (
        <Text style={{ fontSize: 12, color: isDark ? colors.text.secondary : AdminColors.textPrimary }}>
          {item.modifiedBy ? formatUserName(item.modifiedBy) : '-'}
        </Text>
      ),
    },
    {
      columnKey: 'modifiedDate',
      header: 'Modified',
      flexGrow: 2.2,
      renderCell: (item: HpEvent) => (
        <Text style={{ fontSize: 12, color: isDark ? colors.text.secondary : AdminColors.textPrimary }}>
          {item.modifiedDate ? formatDateTime(item.modifiedDate) : '-'}
        </Text>
      ),
    },
    {
      columnKey: '_actions',
      header: 'Actions',
      flexGrow: 1,
      sortable: false,
      renderCell: (item: HpEvent) => (
        <RowActions onEdit={() => setEditItem(item)} onDelete={() => setDeleteTarget(item)} />
      ),
    },
  ];

  return (
    <View>
      {events.length === 0 ? (
        <EmptyState />
      ) : (
        <LHDataTable
          data={sortedEvents}
          columns={eventColumns}
          tableHeader=""
          tableDescription={`Showing ${events.length} result(s)`}
          onRowClick={() => { /* Disable row click overlay */ }}
          disableRowHighlight
        />
      )}

      <AdminModal
        visible={modalVisible}
        title={editItem ? 'Edit Event' : 'Add New Event'}
        onClose={handleClose}
        footer={
          <>
            <AdminButton label="Cancel" variant="secondary" onPress={doClose} disabled={isSaving} />
            <AdminButton label={editItem ? 'Save Changes' : 'Save'} variant="primary" onPress={handleSave} loading={isSaving} />
          </>
        }
      >
        {showUnsavedWarning && (
          <View
            style={[
              localStyles.warningBanner,
              isAnyDark && {
                backgroundColor: colors.statusBackground.warning,
                borderColor: colors.status.warning,
              },
            ]}
          >
            <View style={localStyles.warningBannerContent}>
              <Text style={[localStyles.warningIcon, isAnyDark && { color: colors.status.warning }]}>⚠</Text>
              <Text style={[localStyles.warningMessage, isAnyDark && { color: colors.status.warning }]}>
                You have unsaved changes. To discard and close, click{' '}
                <Text style={localStyles.warningMessageBold}>Cancel</Text>.
              </Text>
            </View>
            <TouchableOpacity onPress={() => setShowUnsavedWarning(false)} style={localStyles.warningCloseBtn}>
              <Text style={[localStyles.warningCloseText, isAnyDark && { color: colors.status.warning }]}>✕</Text>
            </TouchableOpacity>
          </View>
        )}
        <AdminFormField label="Event Name" required error={errors.name}>
          <AdminTextInput
            placeholder="Q2 Safety Leadership Webinar"
            maxLength={100}
            value={eventName}
            onChangeText={(v) => { setEventName(v); if (errors.name) setErrors(e => ({ ...e, name: undefined as any })); }}
            hasError={!!errors.name}
          />
        </AdminFormField>
        <View style={styles.formRow}>
          <View style={styles.formHalf}>
            <AdminFormField label="Event Date" required error={errors.date}>
              <LhDatePicker
                value={eventDate}
                onChange={(date) => { setEventDate(date); if (errors.date) setErrors(e => ({ ...e, date: undefined as any })); }}
                placeholder="Select Event Date"
                colorScheme={isAnyDark ? 'dark' : 'light'}
                iconColor={isAnyDark ? colors.text.primary : '#555'}
                disabledIconColor={isAnyDark ? colors.text.disabled : '#bdbdbd'}
              />
            </AdminFormField>
          </View>
          <View style={styles.formHalf}>
            <AdminFormField label="Event Time" required hint="HH:MM · must be in the future" error={errors.time}>
              <View style={localStyles.timeRow}>
                <View style={localStyles.timeHHMM}>
                  <AdminTextInput
                    placeholder="HH:MM"
                    value={eventHHMM}
                    onChangeText={(v) => { setEventHHMM(v); if (errors.time) setErrors(e => ({ ...e, time: undefined as any })); }}
                    keyboardType="numbers-and-punctuation"
                    maxLength={5}
                    hasError={!!errors.time}
                  />
                </View>
                <SimpleSelect
                  value={eventAmPm}
                  options={['AM', 'PM']}
                  onChange={(v) => { setEventAmPm(v as 'AM' | 'PM'); if (errors.time) setErrors(e => ({ ...e, time: undefined as any })); }}
                  width={60}
                />
                <SimpleSelect
                  value={eventTimezone}
                  options={TIMEZONE_OPTIONS}
                  onChange={setEventTimezone}
                  width={70}
                />
              </View>
            </AdminFormField>
          </View>
        </View>
        <AdminFormField label="Event URL" required hint="Must include domain (.com, .org, etc.)" error={errors.url}>
          <AdminTextInput
            placeholder="https://teams.microsoft.com/..."
            keyboardType="url"
            value={eventUrl}
            onChangeText={(v) => { setEventUrl(v); if (errors.url) setErrors(e => ({ ...e, url: undefined as any })); }}
            hasError={!!errors.url}
          />
        </AdminFormField>
      </AdminModal>

      <DeleteConfirmModal
        visible={deleteTarget !== null}
        itemLabel={deleteTarget?.name ?? ''}
        message="Are you sure you want to delete this event?"
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDeleteConfirm}
        loading={isDeleting}
      />
    </View>
  );
};

const localStyles = StyleSheet.create({
  timeRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  timeHHMM: {
    flex: 1,
    minWidth: 80,
  },
  warningBanner: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    backgroundColor: '#FFF3E0',
    borderWidth: 1,
    borderColor: '#FFB74D',
    borderRadius: 6,
    marginBottom: 12,
    padding: 12,
  },
  warningBannerContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
  },
  warningIcon: {
    fontSize: 15,
    color: '#E65100',
  },
  warningMessage: {
    flex: 1,
    fontSize: 13,
    color: '#E65100',
    lineHeight: 18,
    fontFamily: FontFamily.regular,
  },
  warningMessageBold: {
    fontWeight: '700',
    fontFamily: FontFamily.medium,
  },
  warningCloseBtn: {
    padding: 2,
    marginLeft: 8,
  },
  warningCloseText: {
    fontSize: 14,
    color: '#E65100',
    fontWeight: '700',
  },
});

const ssStyles = StyleSheet.create({
  webWrap: {
    position: 'relative',
    justifyContent: 'center',
  },
  webChevron: {
    position: 'absolute',
    right: 8,
    top: 4,
    fontSize: 14,
    color: AdminColors.textSecondary,
    pointerEvents: 'none' as any,
  },
  nativeWrap: {
    position: 'relative',
    zIndex: 10,
  },
  nativeBtn: {
    height: 36,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 10,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 5,
    backgroundColor: AdminColors.bgSurface,
  },
  nativeBtnLabel: {
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
  },
  nativeChevron: {
    fontSize: 10,
    color: AdminColors.textSecondary,
    marginLeft: 4,
  },
  nativeDropdown: {
    position: 'absolute',
    top: 38,
    left: 0,
    right: 0,
    backgroundColor: AdminColors.bgSurface,
    borderWidth: 1,
    borderColor: AdminColors.borderDefault,
    borderRadius: 5,
    zIndex: 999,
    elevation: 8,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 4,
  },
  nativeOption: {
    paddingHorizontal: 12,
    paddingVertical: 8,
  },
  nativeOptionActive: {
    backgroundColor: '#EFF6FF', // overridden in dark mode inline
  },
  nativeOptionText: {
    fontSize: 13,
    color: AdminColors.textPrimary,
    fontFamily: FontFamily.regular,
  },
  nativeOptionTextActive: {
    color: AdminColors.blue700,
    fontWeight: '600' as const,
    fontFamily: FontFamily.medium,
  },
});

export default EventsTab;
